import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import * as React from "react";
import {
  checkKommoConnection,
  getKommoConfig,
  getKommoPipelines,
  getKommoStatuses,
  saveKommoConfig,
} from "../../../services/kommo.service";
import SectionTitle from "../../../ui-components/SectionTitle";
import { useToast } from "../../../ui-components/ToastProvider";

export default function KommoConfig() {
  const { showToast } = useToast();

  const [config, setConfig] = React.useState({
    subdomain: "",
    accessToken: "",
    pipelineId: 0,
    statusId: 0,
  });

  const [isLoading, setIsLoading] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const [isChecking, setIsChecking] = React.useState(false);
  const [connectionStatus, setConnectionStatus] = React.useState<{
    connected: boolean;
    account?: string;
  } | null>(null);

  const [pipelines, setPipelines] = React.useState<{ id: number; name: string }[]>([]);
  const [statuses, setStatuses] = React.useState<{ id: number; name: string }[]>([]);
  const [loadingPipelines, setLoadingPipelines] = React.useState(false);

  const loadConfig = React.useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await getKommoConfig();
      if (data) {
        setConfig({
          subdomain: data.subdomain || "",
          accessToken: data.accessToken || "",
          pipelineId: data.pipelineId || 0,
          statusId: data.statusId || 0,
        });
      }
    } catch (err: any) {
      showToast(err.message || "Erro ao carregar configuração", { severity: "error" });
    } finally {
      setIsLoading(false);
    }
  }, [showToast]);

  const loadPipelines = React.useCallback(async () => {
    if (!config.subdomain || !config.accessToken) return;
    setLoadingPipelines(true);
    try {
      const list = await getKommoPipelines();
      setPipelines(list);
    } catch {
      // silent
    } finally {
      setLoadingPipelines(false);
    }
  }, [config.subdomain, config.accessToken]);

  const loadStatuses = React.useCallback(
    async (pipelineId: number) => {
      if (!config.subdomain || !config.accessToken || !pipelineId) return;
      try {
        const list = await getKommoStatuses(pipelineId);
        setStatuses(list);
      } catch {
        setStatuses([]);
      }
    },
    [config.subdomain, config.accessToken],
  );

  const handleSave = async () => {
    if (!config.subdomain) {
      showToast("Informe o subdomínio do Kommo", { severity: "warning" });
      return;
    }
    if (!config.accessToken) {
      showToast("Informe o token de acesso", { severity: "warning" });
      return;
    }

    setIsSaving(true);
    try {
      await saveKommoConfig({
        subdomain: config.subdomain.trim(),
        accessToken: config.accessToken.trim(),
        pipelineId: config.pipelineId || undefined,
        statusId: config.statusId || undefined,
      });
      showToast("Configuração salva com sucesso!", { severity: "success" });
    } catch (err: any) {
      showToast(err.message || "Erro ao salvar configuração", { severity: "error" });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCheckConnection = async () => {
    setIsChecking(true);
    try {
      await handleSave();
      const status = await checkKommoConnection();
      setConnectionStatus(status);
      if (status.connected) {
        showToast(`Conectado ao Kommo: ${status.account || "OK"}`, { severity: "success" });
      } else {
        showToast("Falha na conexão com o Kommo", { severity: "error" });
      }
    } catch (err: any) {
      showToast(err.message || "Erro ao testar conexão", { severity: "error" });
      setConnectionStatus({ connected: false });
    } finally {
      setIsChecking(false);
    }
  };

  React.useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  React.useEffect(() => {
    if (config.subdomain && config.accessToken) {
      loadPipelines();
    }
  }, [config.subdomain, config.accessToken, loadPipelines]);

  React.useEffect(() => {
    if (config.pipelineId) {
      loadStatuses(config.pipelineId);
    }
  }, [config.pipelineId, loadStatuses]);

  return (
    <Grid sx={{ p: 2 }}>
      <Paper sx={{ p: 3, borderRadius: 2 }}>
        <SectionTitle title="Kommo CRM — Configuração" />
        <Typography variant="body2" sx={{ color: "#64748b", mb: 2 }}>
          Configure as credenciais do Kommo CRM. Todo novo cadastro será
          automaticamente criado como Contato e Negócio no pipeline selecionado.
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography sx={{ fontWeight: 600, mb: 1, color: "#24324D" }}>
              Credenciais
            </Typography>

            <TextField
              label="Subdomínio Kommo"
              placeholder="empresa"
              value={config.subdomain}
              onChange={(e) =>
                setConfig((prev) => ({ ...prev, subdomain: e.target.value }))
              }
              fullWidth
              size="small"
              sx={{ mb: 2 }}
              helperText="https://[subdomínio].kommo.com"
              disabled={isSaving || isChecking}
            />

            <TextField
              label="Token de Acesso (Long-lived)"
              value={config.accessToken}
              onChange={(e) =>
                setConfig((prev) => ({ ...prev, accessToken: e.target.value }))
              }
              fullWidth
              size="small"
              sx={{ mb: 3 }}
              type="password"
              disabled={isSaving || isChecking}
            />

            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={isSaving || isChecking || isLoading}
                startIcon={isSaving ? <CircularProgress size={16} /> : undefined}
                sx={{ backgroundColor: "#24324D" }}
              >
                {isSaving ? "Salvando..." : "Salvar"}
              </Button>

              <Button
                variant="outlined"
                onClick={handleCheckConnection}
                disabled={isSaving || isChecking || isLoading}
                startIcon={isChecking ? <CircularProgress size={16} /> : undefined}
              >
                {isChecking ? "Testando..." : "Testar Conexão"}
              </Button>

              <Button
                variant="outlined"
                onClick={() => { loadConfig(); loadPipelines(); }}
                disabled={isSaving || isChecking || isLoading}
              >
                Recarregar
              </Button>
            </Box>

            {connectionStatus !== null && (
              <Box sx={{ mt: 2 }}>
                <Chip
                  label={
                    connectionStatus.connected
                      ? `Conectado: ${connectionStatus.account || "OK"}`
                      : "Desconectado"
                  }
                  color={connectionStatus.connected ? "success" : "error"}
                  variant="filled"
                />
              </Box>
            )}
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography sx={{ fontWeight: 600, mb: 1, color: "#24324D" }}>
              Pipeline e Etapa do Negócio
            </Typography>

            <Box sx={{ mb: 2 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={loadPipelines}
                disabled={loadingPipelines || !config.subdomain || !config.accessToken}
                startIcon={loadingPipelines ? <CircularProgress size={14} /> : undefined}
              >
                {loadingPipelines ? "Carregando..." : "Carregar Pipelines"}
              </Button>
            </Box>

            <Typography variant="body2" sx={{ color: "#64748b", mb: 1 }}>
              Pipeline onde os negócios serão criados
            </Typography>
            <Select
              value={config.pipelineId || ""}
              onChange={(e) => {
                const val = Number(e.target.value);
                setConfig((prev) => ({ ...prev, pipelineId: val, statusId: 0 }));
                setStatuses([]);
              }}
              fullWidth
              size="small"
              sx={{ mb: 2 }}
              displayEmpty
              disabled={isSaving || loadingPipelines}
            >
              <MenuItem value="" disabled>
                Selecione um pipeline
              </MenuItem>
              {pipelines.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name}
                </MenuItem>
              ))}
            </Select>

            <Typography variant="body2" sx={{ color: "#64748b", mb: 1 }}>
              Etapa inicial do negócio
            </Typography>
            <Select
              value={config.statusId || ""}
              onChange={(e) =>
                setConfig((prev) => ({ ...prev, statusId: Number(e.target.value) }))
              }
              fullWidth
              size="small"
              displayEmpty
              disabled={isSaving || !config.pipelineId}
            >
              <MenuItem value="" disabled>
                Selecione uma etapa
              </MenuItem>
              {statuses.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}
                </MenuItem>
              ))}
            </Select>

            {config.pipelineId && config.statusId && (
              <Box sx={{ mt: 2 }}>
                <Chip
                  label={`Pipeline: ${pipelines.find((p) => p.id === config.pipelineId)?.name || config.pipelineId} / Etapa: ${statuses.find((s) => s.id === config.statusId)?.name || config.statusId}`}
                  color="primary"
                  variant="outlined"
                  size="small"
                />
              </Box>
            )}
          </Grid>
        </Grid>
      </Paper>
    </Grid>
  );
}
